import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { patchUserData } from "../../app/auth/authSlice";
import "./dashboard-hero-stats.css";

/**
 * Figma hero stats — pure SVG (anti-aliased edges).
 * Frame 224×124 · accent 86×39 top-right · even ~8px gutter.
 * Mint body: #197056 → #30D6A4 · accent: #30D6A4
 */
const PLACEHOLDER_CARDS = [
  {
    id: "gross-sale",
    label: "Total Sales",
    value: "£0.00",
    accent: "mint",
  },
  {
    id: "vat",
    label: "VAT",
    value: "£0.00",
    accent: "silver",
  },
  {
    id: "loyalty",
    label: "Loyalty points given",
    value: "1500",
    accent: "gold",
  },
  {
    id: "online-orders",
    label: "Online Orders",
    value: "36",
    accent: "silver",
  },
];

/**
 * Per-card theme — body = vertical linear gradient, accent = solid tab.
 * Colors from Figma.
 */
const CARD_THEME = {
  mint: { gradientFrom: "#162420", gradientTo: "#101816", accent: "#3AD6A5" },
  silver: { gradientFrom: "#272727", gradientTo: "#1A1A1A", accent: "#A3A3A3" },
  gold: { gradientFrom: "#282517", gradientTo: "#171511", accent: "#FCD535" },
};

/**
 * Body (Subtract) — traced from the real 224×124 export.
 * Horizontal top → ~45° diagonal → horizontal shelf → rounded right block.
 */
function bodyPath() {
  return `
    M 10 0
    L 109 0
    Q 112 0 113 1
    L 160 45
    Q 162 47 164 47
    L 214 47
    A 10 10 0 0 1 224 57
    L 224 114
    A 10 10 0 0 1 214 124
    L 10 124
    A 10 10 0 0 1 0 114
    L 0 10
    A 10 10 0 0 1 10 0
    Z
  `;
}

/**
 * Accent (Rectangle) — traced from the real 86×39 export.
 * Rounded TL/TR/BR, straight diagonal cut on bottom-left.
 */
function accentPath() {
  return `
    M 5 0
    L 80 0
    A 6 6 0 0 1 86 6
    L 86 33
    A 5 5 0 0 1 81 38
    L 31 38
    L 0 10
    L 0 5
    A 5 5 0 0 1 5 0
    Z
  `;
}

function HeroStatShell({ accent, uid }) {
  const theme = CARD_THEME[accent] || CARD_THEME.mint;
  const gradId = `hero-body-grad-${uid}`;
  const glossId = `hero-gloss-${uid}`;
  const accentGlossId = `hero-accent-gloss-${uid}`;
  const borderId = `hero-border-${uid}`;
  const bd = bodyPath();
  const ac = accentPath();

  return (
    <svg
      className="dashboard-hero-stat__shell"
      viewBox="0 0 224 124"
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient
          id={gradId}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
          gradientUnits="objectBoundingBox"
        >
          <stop offset="0%" stopColor={theme.gradientFrom} />
          <stop offset="100%" stopColor={theme.gradientTo} />
        </linearGradient>

        {/* Diagonal glass sheen — bright top-left, fading out */}
        <linearGradient
          id={glossId}
          x1="0"
          y1="0"
          x2="0.85"
          y2="1"
          gradientUnits="objectBoundingBox"
        >
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.28" />
          <stop offset="42%" stopColor="#ffffff" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>

        <linearGradient
          id={accentGlossId}
          x1="0"
          y1="0"
          x2="0.6"
          y2="1"
          gradientUnits="objectBoundingBox"
        >
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>

        {/* Glass edge — highlight only on top-left & bottom-right corners */}
        <linearGradient
          id={borderId}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
          gradientUnits="objectBoundingBox"
        >
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="30%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="70%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.55" />
        </linearGradient>
      </defs>

      {/* Body: colored gradient (slightly translucent) + glass sheen + light edge */}
      <path d={bd} fill={`url(#${gradId})`} fillOpacity="0.92" />
      <path d={bd} fill={`url(#${glossId})`} />
      <path
        d={bd}
        fill="none"
        stroke={`url(#${borderId})`}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />

      {/* Accent flush top-right: 224 - 86 = 138 */}
      <g transform="translate(138 0)">
        <path d={ac} fill={theme.accent} />
        <path d={ac} fill={`url(#${accentGlossId})`} />
        <path
          d={ac}
          fill="none"
          stroke={`url(#${borderId})`}
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      </g>
    </svg>
  );
}

function firstWord(value) {
  const name = String(value || "").trim();
  if (!name) return "";
  return name.split(/\s+/)[0];
}

/** Prefer logged-in staff FirstName; fall back to company OwnerName. */
function resolveGreetingFirstName(user, profileData) {
  const ud = user?.userData || user?.UserData || {};
  const staff =
    firstWord(ud.firstName) ||
    firstWord(ud.FirstName) ||
    firstWord(ud.firstname);
  const owner =
    firstWord(profileData?.ownerName) ||
    firstWord(profileData?.OwnerName);
  return staff || owner || "there";
}

function DashboardHeroStats({ cards = PLACEHOLDER_CARDS }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const profileData = useSelector((state) => state.auth.profileData);
  const ud = user?.userData || user?.UserData || {};
  // Primitive deps only — patching userData must not re-fire this effect (API loop).
  const CompID = ud.compID || ud.CompID;
  const UserName = ud.userName || ud.UserName;
  const fetchedKeyRef = useRef("");
  const [firstName, setFirstName] = useState(() =>
    resolveGreetingFirstName(user, profileData)
  );

  // Refresh staff FirstName with CompID scope (login lookup is username-only).
  useEffect(() => {
    if (!CompID || !UserName) {
      setFirstName(resolveGreetingFirstName(user, profileData));
      return;
    }

    const fetchKey = `${CompID}:${UserName}`;
    if (fetchedKeyRef.current === fetchKey) return;
    fetchedKeyRef.current = fetchKey;

    const existingFirst =
      firstWord(ud.firstName) || firstWord(ud.FirstName) || "";

    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(APIs.GET_STAFF_DATA, {
          params: { CompID, UserName },
        });
        const staff = res.data?.data || res.data?.Data || res.data || {};
        const freshFirst = staff.firstName || staff.FirstName || "";
        const fresh =
          firstWord(staff.firstName) ||
          firstWord(staff.FirstName) ||
          "";
        if (cancelled) return;
        if (fresh) {
          setFirstName(fresh);
          // Skip Redux write when unchanged — avoids user-ref churn + TokenExpiry re-poll.
          if (fresh !== existingFirst) {
            dispatch(
              patchUserData({
                firstName: freshFirst || fresh,
                FirstName: staff.FirstName || staff.firstName || fresh,
                lastName: staff.lastName || staff.LastName,
                LastName: staff.LastName || staff.lastName,
              })
            );
          }
        } else {
          setFirstName(resolveGreetingFirstName(user, profileData));
        }
      } catch {
        if (!cancelled) {
          fetchedKeyRef.current = "";
          setFirstName(resolveGreetingFirstName(user, profileData));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only CompID/UserName
  }, [CompID, UserName, dispatch]);

  return (
    <section className="dashboard-hero-stats" aria-label="Dashboard summary">
      <h1 className="dashboard-hero-stats__greeting">
        Hi {firstName}, <span aria-hidden="true">👋</span>
      </h1>

      <div className="dashboard-hero-stats__grid">
        {cards.map((card) => (
          <article
            key={card.id}
            className={`dashboard-hero-stat dashboard-hero-stat--${card.accent}`}
          >
            <HeroStatShell accent={card.accent} uid={card.id} />
            <div className="dashboard-hero-stat__content">
              <div className="dashboard-hero-stat__label">{card.label}</div>
              <div className="dashboard-hero-stat__value">{card.value}</div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default DashboardHeroStats;
